/**
 * 后台 - 视频模型列表
 */

import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Loader2, Plus, Settings, Trash2 } from 'lucide-react';
import {
  videoModelAdminApi,
  type VideoModelAdminView,
} from '../services/videoAdminApi';
import VideoModelForm from './VideoModelForm';
import toast from '../../../components/Toast';

export default function VideoModelsTab() {
  const [models, setModels] = useState<VideoModelAdminView[]>([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [editing, setEditing] = useState<VideoModelAdminView | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await videoModelAdminApi.list();
      setModels(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载模型失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('确定删除这个视频模型吗？')) return;
    try {
      await videoModelAdminApi.remove(id);
      await load();
      toast.success('已删除');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handleToggleActive = async (model: VideoModelAdminView) => {
    if (togglingId) return;
    setTogglingId(model.id);

    const nextActive = !model.isActive;
    // 乐观更新
    setModels((prev) =>
      prev.map((m) => (m.id === model.id ? { ...m, isActive: nextActive } : m))
    );

    try {
      await videoModelAdminApi.update(model.id, { isActive: nextActive });
      toast.success(nextActive ? '已启用' : '已禁用');
    } catch (err) {
      // 回滚
      setModels((prev) =>
        prev.map((m) => (m.id === model.id ? { ...m, isActive: model.isActive } : m))
      );
      toast.error(err instanceof Error ? err.message : '切换状态失败');
    } finally {
      setTogglingId(null);
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= models.length || reordering) return;

    const previous = models;
    const next = reorderModels(previous, index, targetIndex);
    setModels(next);
    setReordering(true);

    try {
      await persistModelOrder(next);
      toast.success(direction === 'up' ? '已上移' : '已下移');
      await load();
    } catch (err) {
      setModels(previous);
      toast.error(err instanceof Error ? err.message : '更新排序失败');
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">视频模型配置</h2>
          <p className="text-sm text-light-muted mt-1">
            维护项目内的视频模型，含计费系数、能力开关、各模式渠道
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          新增模型
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-light-muted">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : models.length === 0 ? (
        <div className="py-12 text-center text-light-muted text-sm">
          还未配置视频模型
        </div>
      ) : (
        <div className="bg-white border border-light-border rounded-2xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-light-border text-sm">
              <thead className="bg-light-hover">
                <tr className="text-left text-xs uppercase tracking-wider text-light-muted">
                  <th className="px-4 py-3">排序</th>
                  <th className="px-4 py-3">模型 ID</th>
                  <th className="px-4 py-3">名称</th>
                  <th className="px-4 py-3">默认渠道</th>
                  <th className="px-4 py-3">计费方式</th>
                  <th className="px-4 py-3">支持分辨率</th>
                  <th className="px-4 py-3">支持模式</th>
                  <th className="px-4 py-3">状态</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border">
                {models.map((m, index) => (
                  <tr key={m.id} className="hover:bg-light-hover/50">
                    <td className="px-4 py-3">
                      <SortCell
                        value={m.sortOrder}
                        canMoveUp={index > 0}
                        canMoveDown={index < models.length - 1}
                        disabled={reordering}
                        onMoveUp={() => handleMove(index, 'up')}
                        onMoveDown={() => handleMove(index, 'down')}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-light-secondary">{m.modelId}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-light-text">{m.name}</div>
                      {m.description && (
                        <div className="text-xs text-light-muted mt-0.5 line-clamp-1">{m.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-light-secondary">
                      {m.defaultChannel ? `${m.defaultChannel.name} · ${m.defaultChannel.apiModelId}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-light-text">{getBillingModeLabel(m.billingMode)}</div>
                      <div className="text-xs text-light-muted">
                        {m.billingMode === 'per_call' ? `${m.creditsPerCall} 积分/次起` : `${m.creditsPerSecond} 积分/秒起`}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ResolutionList
                        resolutionMultipliers={m.resolutionMultipliers}
                        disabledResolutions={m.disabledResolutions}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <ModeList modeCapabilities={m.modeCapabilities} />
                    </td>
                    <td className="px-4 py-3">
                      <ToggleSwitch
                        isActive={m.isActive}
                        disabled={togglingId === m.id}
                        onToggle={() => handleToggleActive(m)}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ModelActions
                        model={m}
                        canMoveUp={index > 0}
                        canMoveDown={index < models.length - 1}
                        disabled={reordering}
                        onEdit={(model) => {
                          setEditing(model);
                          setShowForm(true);
                        }}
                        onMoveUp={() => handleMove(index, 'up')}
                        onMoveDown={() => handleMove(index, 'down')}
                        onDelete={handleDelete}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {models.map((m, index) => (
              <div key={m.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-light-text break-all">{m.name}</div>
                    <div className="mt-1 font-mono text-xs text-light-muted break-all">{m.modelId}</div>
                    {m.description && <div className="mt-1 text-xs text-light-muted">{m.description}</div>}
                  </div>
                  <ToggleSwitch
                    isActive={m.isActive}
                    disabled={togglingId === m.id}
                    onToggle={() => handleToggleActive(m)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InfoBlock label="排序" value={String(m.sortOrder)} />
                  <InfoBlock
                    label="默认渠道"
                    value={m.defaultChannel ? `${m.defaultChannel.name} · ${m.defaultChannel.apiModelId}` : '-'}
                    mono
                  />
                  <InfoBlock
                    label="计费方式"
                    value={`${getBillingModeLabel(m.billingMode)} · ${
                      m.billingMode === 'per_call' ? `${m.creditsPerCall} 积分/次起` : `${m.creditsPerSecond} 积分/秒起`
                    }`}
                  />
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="mb-1 text-xs text-light-muted">支持分辨率</div>
                    <ResolutionList
                      resolutionMultipliers={m.resolutionMultipliers}
                      disabledResolutions={m.disabledResolutions}
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-light-muted">支持模式</div>
                    <ModeList modeCapabilities={m.modeCapabilities} />
                  </div>
                </div>
                <ModelActions
                  model={m}
                  canMoveUp={index > 0}
                  canMoveDown={index < models.length - 1}
                  disabled={reordering}
                  onEdit={(model) => {
                    setEditing(model);
                    setShowForm(true);
                  }}
                  onMoveUp={() => handleMove(index, 'up')}
                  onMoveDown={() => handleMove(index, 'down')}
                  onDelete={handleDelete}
                  mobile
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <VideoModelForm
          model={editing}
          onClose={(changed) => {
            setShowForm(false);
            setEditing(null);
            if (changed) load();
          }}
        />
      )}
    </div>
  );
}

async function persistModelOrder(models: VideoModelAdminView[]) {
  await Promise.all(
    models.map((model, index) =>
      videoModelAdminApi.update(model.id, {
        sortOrder: (index + 1) * 10,
      })
    )
  );
}

function reorderModels(models: VideoModelAdminView[], fromIndex: number, toIndex: number): VideoModelAdminView[] {
  const next = [...models];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);

  return next.map((model, index) => ({
    ...model,
    sortOrder: (index + 1) * 10,
  }));
}

function getBillingModeLabel(mode: VideoModelAdminView['billingMode']): string {
  return mode === 'per_call' ? '按次计费' : '按秒计费';
}

function ModeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-1.5 py-0.5 rounded text-[11px] bg-blue-50 text-blue-700">{children}</span>
  );
}

function ToggleSwitch({
  isActive,
  disabled,
  onToggle,
}: {
  isActive: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        isActive ? 'bg-emerald-500' : 'bg-zinc-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
      aria-pressed={isActive}
      title={isActive ? '点击禁用' : '点击启用'}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
          isActive ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function InfoBlock({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className={`mt-1 text-sm text-light-text break-all ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function SortCell({
  value,
  canMoveUp,
  canMoveDown,
  disabled,
  onMoveUp,
  onMoveDown,
}: {
  value: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex min-w-10 justify-center rounded-md bg-light-hover px-2 py-1 text-xs font-medium text-light-text">
        {value}
      </span>
      <div className="flex items-center gap-1">
        <MoveButton
          label="上移"
          icon={<ArrowUp className="w-3.5 h-3.5" />}
          disabled={disabled || !canMoveUp}
          onClick={onMoveUp}
        />
        <MoveButton
          label="下移"
          icon={<ArrowDown className="w-3.5 h-3.5" />}
          disabled={disabled || !canMoveDown}
          onClick={onMoveDown}
        />
      </div>
    </div>
  );
}

function ResolutionList({
  resolutionMultipliers,
  disabledResolutions,
}: {
  resolutionMultipliers: Record<string, number>;
  disabledResolutions?: string[];
}) {
  const resolutions = getResolutionList(resolutionMultipliers, disabledResolutions);
  if (resolutions.length === 0) return <span className="text-xs text-light-muted">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {resolutions.map((resolution) => (
        <ResolutionBadge key={resolution} resolution={resolution} />
      ))}
    </div>
  );
}

function ModeList({
  modeCapabilities,
}: {
  modeCapabilities?: VideoModelAdminView['modeCapabilities'];
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {modeCapabilities?.text2video && <ModeBadge>文生</ModeBadge>}
      {modeCapabilities?.img2video && <ModeBadge>图生</ModeBadge>}
      {modeCapabilities?.first_last_frame && <ModeBadge>首尾帧</ModeBadge>}
      {!modeCapabilities?.text2video && !modeCapabilities?.img2video && !modeCapabilities?.first_last_frame && (
        <span className="text-xs text-light-muted">-</span>
      )}
    </div>
  );
}

function ModelActions({
  model,
  canMoveUp,
  canMoveDown,
  disabled,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
  mobile = false,
}: {
  model: VideoModelAdminView;
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled: boolean;
  onEdit: (model: VideoModelAdminView) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: (id: string) => void;
  mobile?: boolean;
}) {
  const baseClass = mobile
    ? 'inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover'
    : 'p-1.5 rounded-lg hover:bg-light-hover text-light-secondary';
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'inline-flex items-center gap-1.5'}>
      {mobile && (
        <>
          <button
            onClick={onMoveUp}
            disabled={disabled || !canMoveUp}
            className="inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowUp className="w-4 h-4" />
            上移
          </button>
          <button
            onClick={onMoveDown}
            disabled={disabled || !canMoveDown}
            className="inline-flex items-center gap-1.5 rounded-lg border border-light-border px-3 py-2 text-xs text-light-secondary hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowDown className="w-4 h-4" />
            下移
          </button>
        </>
      )}
      <button onClick={() => onEdit(model)} className={baseClass}>
        <Settings className="w-4 h-4" />
        {mobile && '编辑'}
      </button>
      <button
        onClick={() => onDelete(model.id)}
        className={mobile
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs text-rose-500 hover:bg-rose-50'
          : 'p-1.5 rounded-lg hover:bg-rose-50 text-rose-500'}
      >
        <Trash2 className="w-4 h-4" />
        {mobile && '删除'}
      </button>
    </div>
  );
}

function MoveButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-light-border text-light-secondary hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
    >
      {icon}
    </button>
  );
}

function getResolutionList(
  resolutionMultipliers: Record<string, number>,
  disabledResolutions: string[] = []
): string[] {
  const preferredOrder = ['480p', '720p', '1080p', '4k'];
  const normalizedMap = new Map<string, string>();
  const disabledSet = new Set(
    disabledResolutions
      .map((item) => String(item || '').trim().toLowerCase())
      .filter(Boolean)
  );

  for (const key of Object.keys(resolutionMultipliers || {})) {
    const trimmed = String(key || '').trim();
    if (!trimmed) continue;
    if (disabledSet.has(trimmed.toLowerCase())) continue;
    normalizedMap.set(trimmed.toLowerCase(), trimmed);
  }

  const ordered = preferredOrder
    .filter((value) => normalizedMap.has(value.toLowerCase()))
    .map((value) => normalizedMap.get(value.toLowerCase())!);

  const extras = Array.from(normalizedMap.entries())
    .filter(([key]) => !preferredOrder.includes(key))
    .map(([, value]) => value)
    .sort((a, b) => a.localeCompare(b));

  return [...ordered, ...extras];
}

function ResolutionBadge({ resolution }: { resolution: string }) {
  const normalized = resolution.trim().toLowerCase();

  let className = 'bg-zinc-100 text-zinc-700';
  if (normalized === '480p') className = 'bg-rose-100 text-rose-700';
  else if (normalized === '720p') className = 'bg-emerald-100 text-emerald-700';
  else if (normalized === '1080p') className = 'bg-blue-100 text-blue-700';
  else if (normalized === '4k') className = 'bg-violet-100 text-violet-700';

  return (
    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${className}`}>
      {resolution}
    </span>
  );
}
